import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Tag, X } from 'lucide-react';

export default function TagInput({
  tags,
  onAddTag,
  onRemoveTag,
  options = [],
  placeholder = 'Type a tag and press Enter…',
  emptyHint = 'No match - press Enter to create this tag.',
  className = '',
}) {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [menuPos, setMenuPos] = useState(null);
  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const inputRef = useRef(null);
  const currentTags = useMemo(() => (Array.isArray(tags) ? tags : []), [tags]);
  const normalizedOptions = useMemo(() => {
    const seen = new Set();
    const existing = new Set(currentTags.map((tag) => String(tag).toLowerCase()));
    return (options || [])
      .map((option) => (typeof option === 'string' ? option : option?.value || option?.label || ''))
      .map((option) => String(option).trim())
      .filter((option) => {
        const key = option.toLowerCase();
        if (!option || seen.has(key) || existing.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [options, currentTags]);

  const filtered = useMemo(() => {
    const query = input.trim().toLowerCase();
    if (!query) return normalizedOptions.slice(0, 8);
    const starts = [];
    const wordBoundary = [];
    const contains = [];
    for (const option of normalizedOptions) {
      const haystack = option.toLowerCase();
      if (haystack === query) starts.unshift(option);
      else if (haystack.startsWith(query)) starts.push(option);
      else if (haystack.includes(` ${query}`)) wordBoundary.push(option);
      else if (haystack.includes(query)) contains.push(option);
    }
    return [...starts, ...wordBoundary, ...contains].slice(0, 8);
  }, [input, normalizedOptions]);

  const updateMenuPos = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const gap = 4;
    const below = window.innerHeight - rect.bottom;
    const above = rect.top;
    const maxHeight = 240;
    const openUp = below < maxHeight && above > below;
    setMenuPos({
      left: rect.left,
      top: openUp ? rect.top - gap : rect.bottom + gap,
      width: rect.width,
      maxHeight: Math.min(maxHeight, Math.max(120, openUp ? above - gap - 8 : below - gap - 8)),
      openUp,
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    updateMenuPos();
    const onDown = (event) => {
      const inContainer = containerRef.current && containerRef.current.contains(event.target);
      const inMenu = menuRef.current && menuRef.current.contains(event.target);
      if (!inContainer && !inMenu) setOpen(false);
    };
    const onScrollOrResize = () => updateMenuPos();
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updateMenuPos]);

  useEffect(() => {
    setHighlight((current) => Math.min(current, Math.max(0, filtered.length - 1)));
  }, [filtered.length]);

  const addTag = useCallback((value) => {
    const tag = String(value || '').trim().replace(/,$/, '').trim();
    if (!tag) return;
    const exists = currentTags.some((existing) => String(existing).toLowerCase() === tag.toLowerCase());
    if (!exists) onAddTag(tag);
    setInput('');
    setOpen(false);
    inputRef.current?.focus();
  }, [currentTags, onAddTag]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && input.trim()) {
      event.preventDefault();
      addTag(open && filtered[highlight] ? filtered[highlight] : input);
    } else if (event.key === ',' && input.trim()) {
      event.preventDefault();
      addTag(input);
    } else if (event.key === 'Backspace' && !input && currentTags.length > 0) {
      onRemoveTag(currentTags[currentTags.length - 1]);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setHighlight((current) => Math.min(Math.max(0, filtered.length - 1), current + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setHighlight((current) => Math.max(0, current - 1));
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
    }
  };

  const showMenu = open && (filtered.length > 0 || (input.trim() && emptyHint));

  return (
    <div
      className={className}
      style={{ position: 'relative' }}
    >
      <div
        ref={containerRef}
        onClick={() => inputRef.current?.focus()}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 6,
          padding: 6,
          minHeight: 36,
          background: 'var(--surface-panel)',
          border: 'var(--stroke-xs) solid var(--border-primary)',
          borderRadius: 6,
          cursor: 'text',
          transition: 'border-color 120ms',
        }}
      >
        {currentTags.map((tag, index) => (
          <span key={index} className="chip gray" style={{ gap: 4 }}>
            <Tag className="w-3 h-3" />
            {tag}
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onRemoveTag(tag);
              }}
              style={{
                marginLeft: 2,
                display: 'inline-flex',
                alignItems: 'center',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-tertiary)',
              }}
              title={`Remove ${tag}`}
              aria-label={`Remove ${tag}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(event) => {
            setInput(event.target.value);
            setOpen(true);
            setHighlight(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={currentTags.length === 0 ? placeholder : ''}
          style={{
            flex: 1,
            minWidth: 120,
            padding: '4px 6px',
            fontSize: 13,
            outline: 'none',
            border: 'none',
            background: 'transparent',
            color: 'var(--text-primary)',
          }}
        />
        {input.trim() && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              addTag(input);
            }}
            className="icon-btn xs"
            title="Add tag"
            aria-label="Add tag"
            style={{
              flexShrink: 0,
            }}
          >
            <Plus className="w-3 h-3" />
          </button>
        )}
      </div>
      {showMenu && menuPos && (
        <div
          ref={menuRef}
          role="listbox"
          style={{
            position: 'fixed',
            top: menuPos.openUp ? 'auto' : menuPos.top,
            bottom: menuPos.openUp ? (window.innerHeight - menuPos.top) : 'auto',
            left: menuPos.left,
            width: menuPos.width,
            zIndex: 1000,
            background: 'var(--surface-base)',
            border: 'var(--stroke-xs) solid var(--border-primary)',
            borderRadius: 'var(--corner-radius-md, 8px)',
            boxShadow: 'var(--shadow-md, 0 6px 16px rgba(0,0,0,0.1))',
            maxHeight: menuPos.maxHeight,
            overflowY: 'auto',
            padding: 4,
          }}
        >
          {filtered.length === 0 && input.trim() && emptyHint && (
            <div className="muted" style={{ padding: '8px 10px', fontSize: 12 }}>
              {emptyHint}
            </div>
          )}
          {filtered.map((tag, index) => (
            <div
              key={`${tag}-${index}`}
              role="option"
              aria-selected={index === highlight}
              onMouseDown={(event) => { event.preventDefault(); addTag(tag); }}
              onMouseEnter={() => setHighlight(index)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                borderRadius: 'var(--corner-radius-sm, 6px)',
                cursor: 'pointer',
                background: index === highlight ? 'var(--surface-hover)' : 'transparent',
              }}
            >
              <Tag className="w-3 h-3" style={{ color: 'var(--icon-tertiary)', flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {tag}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
